import { useMemo } from "react";
import { SortOrder, ColumnType } from "antd/lib/table/interface";

interface UseTableSortingProps {
  // Column configuration
  columns?: string[];
  config: {
    sortBy?: string;
    sortDirection?: "ascending" | "descending";
    sortColumns?: boolean;
  };
  // Data-specific props
  hasCompareClause?: boolean;
  showChangeAs?: "value" | "percentage";
  showChangeValues?: boolean;
}

export function useTableSorting({
  columns = [],
  config,
  hasCompareClause,
  showChangeAs,
  showChangeValues,
}: UseTableSortingProps) {
  // Column order sorting (used by MeasureTable)
  const columnOrderSorter = useMemo(() => {
    return (a: ColumnType<any>, b: ColumnType<any>) => {
      const aKey = `${a.key || ""}`;
      const bKey = `${b.key || ""}`;
      const aConfigCol = columns.find((c) => c.startsWith(aKey));
      const bConfigCol = columns.find((c) => c.startsWith(bKey));
      const aIndex = aConfigCol ? columns.indexOf(aConfigCol) : -1;
      const bIndex = bConfigCol ? columns.indexOf(bConfigCol) : -1;
      return columns.length === 0 ? 0 : aIndex - bIndex;
    };
  }, [columns]);

  // Numeric value sorting (used by PivotTable)
  const numericValueSorter = useMemo(() => {
    return (a: any, b: any, key: string) => {
      const aVal = hasCompareClause ? `${a[key]}`.split(",")[1] : a[key];
      const bVal = hasCompareClause ? `${b[key]}`.split(",")[1] : b[key];
      return Number(bVal || 0) - Number(aVal || 0);
    };
  }, [hasCompareClause]);

  // Ant Design sort order (used by EntitiesTable)
  const sortOrder = useMemo(() => {
    if (!config.sortBy) return null;
    return (
      config.sortDirection === "ascending" ? "ascend" : "descend"
    ) as SortOrder;
  }, [config.sortBy, config.sortDirection]);

  return {
    columnOrderSorter,
    numericValueSorter,
    sortOrder,
    // Helper to determine if sorting is enabled
    isSortingEnabled: Boolean(config.sortColumns),
  };
}
